import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Svg, { Circle, Line, Polyline, Text as SvgText } from 'react-native-svg';

import { ProgressBackButton } from '@/components/progress/ProgressBackButton';
import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { ROUTES, reportRoute } from '@/navigation/routes';
import { formatLevel, normalizeLevelKey, type LevelKey } from '@/utils/icfLevels';

const CHART_ORDINAL: Record<LevelKey, number> = {
  below_acc: 0,
  acc: 1,
  ready_for_pcc: 1.5,
  pcc: 2,
  pcc_upper_mcc_adjacent: 2.5,
  mcc: 3,
};
import { ICFProgressAPI } from '@/services/api/icfProgressAPI';
import { trackAuthEvent } from '@/services/analytics';
import { COMPETENCY_KEY_TO_CODE, CrfGateRow } from '@/components/progress/CrfReadinessCard';
import { ICF_FORMS, IcfCompetencyCode } from '@/data/icfFormStatements';
import {
  CompetencyDetailEvidenceItem,
  CompetencyDetailResponse,
  CrfReadiness,
  ICFCompetency,
} from '@/types/icfProgress';

const COMPETENCY_LABEL_KEYS: Record<ICFCompetency, string> = {
  [ICFCompetency.DEMONSTRATES_ETHICAL_PRACTICE]: 'icfProgress.compEthicsFull',
  [ICFCompetency.EMBODIES_COACHING_MINDSET]: 'icfProgress.compMindsetFull',
  [ICFCompetency.ESTABLISHES_MAINTAINS_AGREEMENTS]: 'icfProgress.compAgreementsFull',
  [ICFCompetency.CULTIVATES_TRUST_SAFETY]: 'icfProgress.compTrustFull',
  [ICFCompetency.MAINTAINS_PRESENCE]: 'icfProgress.compPresenceFull',
  [ICFCompetency.LISTENS_ACTIVELY]: 'icfProgress.compListeningFull',
  [ICFCompetency.EVOKES_AWARENESS]: 'icfProgress.compAwarenessFull',
  [ICFCompetency.FACILITATES_CLIENT_GROWTH]: 'icfProgress.compGrowthFull',
};

function firstParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

export default function CompetencyDetailScreen() {
  const params = useLocalSearchParams<{ competency?: string | string[] }>();
  const router = useRouter();
  const { t, language } = useTranslation();
  const { isPhone, isTablet, isDesktop } = useResponsive();
  const competency = firstParam(params.competency) as ICFCompetency | undefined;
  const isValidCompetency = Boolean(competency && COMPETENCY_LABEL_KEYS[competency]);
  const [detail, setDetail] = useState<CompetencyDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    acc: !isPhone,
    pcc: false,
    mcc: false,
  });
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const [crfReadiness, setCrfReadiness] = useState<CrfReadiness | null>(null);
  const trackedView = useRef(false);

  // The CRF block rides on the readiness snapshot, not on the competency detail
  // payload; the dashboard call is cached so this is cheap.
  useEffect(() => {
    let active = true;
    ICFProgressAPI.getDashboard()
      .then((response) => {
        if (active) setCrfReadiness(response?.readiness?.crf_readiness || null);
      })
      .catch(() => {
        if (active) setCrfReadiness(null);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    if (!isValidCompetency || !competency) {
      setError(t('icfProgress.detail.invalidCompetency'));
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    ICFProgressAPI.getCompetencyDetail(competency, language === 'en' ? 'en' : 'tr')
      .then((response) => {
        if (!active) return;
        setDetail(response);
        setError(null);
      })
      .catch(() => {
        if (active) setError(t('icfProgress.detail.loadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [competency, isValidCompetency, language, t]);

  useEffect(() => {
    if (!detail || trackedView.current) return;
    trackedView.current = true;
    trackAuthEvent('progress_competency_detail_viewed', {
      competency: detail.competency,
      level: detail.lane.current_level_key,
      evidence_count: detail.evidence.length,
    });
  }, [detail]);

  const title = competency && COMPETENCY_LABEL_KEYS[competency]
    ? t(COMPETENCY_LABEL_KEYS[competency])
    : t('icfProgress.detail.title');
  const momentum = detail?.lane.momentum?.direction as string | undefined;
  const momentumIcon = momentum === 'up' ? 'arrow-upward' : momentum === 'down' ? 'arrow-downward' : 'remove';
  const momentumLabel = momentum === 'up'
    ? t('icfProgress.momentumUp')
    : momentum === 'down'
      ? t('icfProgress.momentumDown')
      : t('icfProgress.momentumFlat');

  // CORE-2: chart rows are below ACC (0), ACC (1), PCC (2), MCC (3); in-between
  // bands sit between rows instead of being plotted as the next level up.
  const historyPoints = useMemo(() => detail?.session_history.map((item) => {
    const key = normalizeLevelKey(item.level_key);
    const ordinal = key ? CHART_ORDINAL[key] : 0;
    return { ...item, ordinal };
  }) || [], [detail?.session_history]);

  const panel = (children: React.ReactNode, testID?: string) => (
    <View testID={testID} style={styles.panel}>{children}</View>
  );

  const renderHistory = () => panel(
    <>
      <Text selectable style={styles.panelTitle}>{t('icfProgress.detail.levelHistory')}</Text>
      <Text selectable style={styles.panelSubtitle}>{t('icfProgress.detail.levelHistoryDescription')}</Text>
      {!detail?.session_history.length ? (
        <View style={styles.emptyState} testID="competency_detail_empty_history">
          <MaterialIcons name="timeline" size={24} color={reviewDesk.muted} />
          <Text selectable style={styles.emptyText}>
            {detail?.trend_state.message || t('icfProgress.detail.lockedTrend')}
          </Text>
        </View>
      ) : (
        <View
          accessible
          accessibilityRole="image"
          accessibilityLabel={t('icfProgress.detail.levelHistoryAccessibility')}
          style={styles.historyChart}
          testID="competency_detail_history"
        >
          <Svg width="100%" height={190} viewBox="0 0 680 190" preserveAspectRatio="none">
            {[0, 1, 2, 3].map((ordinal) => {
              const y = 18 + ((3 - ordinal) / 3) * 132;
              const label = ordinal === 3
                ? t('icfProgress.detail.levelMccShort')
                : ordinal === 2
                  ? t('icfProgress.detail.levelPccShort')
                  : ordinal === 1
                    ? t('icfProgress.detail.levelAccShort')
                    : t('icfProgress.detail.levelBelowAccShort');
              return (
                <React.Fragment key={label}>
                  <Line x1={54} x2={662} y1={y} y2={y} stroke={reviewDesk.line} strokeWidth={1} />
                  <SvgText x={8} y={y + 4} fill={reviewDesk.muted} fontSize={11} fontWeight="700">{label}</SvgText>
                </React.Fragment>
              );
            })}
            {historyPoints.length > 1 && (
              <Polyline
                points={historyPoints.map((item, index) => {
                  const x = 54 + (index / (historyPoints.length - 1)) * 608;
                  const y = 18 + ((3 - item.ordinal) / 3) * 132;
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke={reviewDesk.accent}
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {historyPoints.map((item, index) => {
              const x = historyPoints.length === 1 ? 358 : 54 + (index / (historyPoints.length - 1)) * 608;
              const y = 18 + ((3 - item.ordinal) / 3) * 132;
              return (
                <Circle
                  key={`${item.session_id || index}-${item.level_key}`}
                  cx={x}
                  cy={y}
                  r={5}
                  fill={reviewDesk.card}
                  stroke={reviewDesk.accent}
                  strokeWidth={3}
                />
              );
            })}
          </Svg>
          <View style={styles.historyDateRow}>
            <Text style={styles.historyDate}>
              {historyPoints[0]?.analyzed_at ? new Date(historyPoints[0].analyzed_at).toLocaleDateString(language) : ''}
            </Text>
            <Text style={styles.historyDate}>
              {historyPoints[historyPoints.length - 1]?.analyzed_at
                ? new Date(historyPoints[historyPoints.length - 1].analyzed_at!).toLocaleDateString(language)
                : ''}
            </Text>
          </View>
        </View>
      )}
      <Text selectable style={styles.trendMessage}>{detail?.trend_state.message}</Text>
    </>,
    'competency_detail_level_history',
  );

  const openEvidence = (item: CompetencyDetailEvidenceItem) => {
    if (!item.session_id) return;
    trackAuthEvent('progress_competency_evidence_clicked', { competency });
    router.push(reportRoute(item.session_id) as never);
  };

  const renderEvidence = () => panel(
    <>
      <Text selectable style={styles.panelTitle}>{t('icfProgress.detail.evidenceTitle')}</Text>
      {!detail?.evidence.length ? (
        <Text selectable style={styles.emptyText}>{t('icfProgress.detail.emptyEvidence')}</Text>
      ) : detail.evidence.slice(0, evidenceExpanded ? detail.evidence.length : 4).map((item, index) => {
        const isStrength = item.polarity === 'strength';
        return (
          <Pressable
            key={`${item.session_id || 'evidence'}-${index}`}
            disabled={!item.session_id}
            onPress={() => openEvidence(item)}
            style={({ pressed }) => [
              styles.evidenceCard,
              { borderLeftColor: isStrength ? reviewDesk.success : reviewDesk.amber },
              pressed && styles.pressed,
            ]}
          >
            <View style={styles.evidenceMeta}>
              <Text selectable style={[styles.pill, { color: isStrength ? reviewDesk.success : '#9A6500' }]}>
                {isStrength ? t('icfProgress.detail.strength') : t('icfProgress.detail.development')}
              </Text>
              <Text selectable style={styles.metaText}>{formatLevel(t, item.level_key)}</Text>
            </View>
            <Text selectable numberOfLines={evidenceExpanded ? undefined : 3} style={styles.quote}>“{item.quote}”</Text>
            <Text selectable style={styles.metaText}>
              {item.session_title || t('icfProgress.detail.sessionFallback')}
              {item.analyzed_at ? ` · ${new Date(item.analyzed_at).toLocaleDateString(language)}` : ''}
            </Text>
          </Pressable>
        );
      })}
      {(detail?.evidence.length || 0) > 4 && (
        <Pressable
          accessibilityRole="button"
          onPress={() => setEvidenceExpanded((current) => !current)}
          style={({ pressed }) => [styles.showMoreButton, pressed && styles.pressed]}
        >
          <Text style={styles.showMoreText}>
            {evidenceExpanded
              ? t('icfProgress.detail.showLessEvidence')
              : t('icfProgress.detail.showMoreEvidence', { count: detail!.evidence.length - 4 })}
          </Text>
          <MaterialIcons name={evidenceExpanded ? 'expand-less' : 'expand-more'} size={20} color={reviewDesk.accent} />
        </Pressable>
      )}
    </>,
    'competency_detail_evidence',
  );

  const renderPractice = () => panel(
    <>
      <Text selectable style={styles.panelTitle}>{t('icfProgress.detail.practiceNext')}</Text>
      {!detail?.practice_suggestions.length ? (
        <Text selectable style={styles.emptyText}>{t('icfProgress.detail.emptyPractice')}</Text>
      ) : detail.practice_suggestions.map((item, index) => (
        <View key={`${item.title}-${index}`} style={styles.practiceItem}>
          <View style={styles.practiceNumber}><Text style={styles.practiceNumberText}>{index + 1}</Text></View>
          <View style={styles.flexOne}>
            <Text selectable style={styles.itemTitle}>{item.title}</Text>
            <Text selectable style={styles.bodyText}>{item.body}</Text>
            <Text selectable style={styles.successText}>
              {t('icfProgress.detail.successSignal')}: {item.success_signal}
            </Text>
          </View>
        </View>
      ))}
    </>,
    'competency_detail_practice',
  );

  const competencyCode = competency ? COMPETENCY_KEY_TO_CODE[competency] : undefined;
  const crfGate = crfReadiness?.per_competency.find((item) => item.code === competencyCode);
  const crfStatements = useMemo(() => {
    if (!crfReadiness || !competencyCode) return [];
    const block = ICF_FORMS[crfReadiness.level].competencies
      .find((item) => item.code === (competencyCode as IcfCompetencyCode));
    return block?.statements || [];
  }, [crfReadiness, competencyCode]);

  /**
   * Same gate the Progress card shows, plus the per-statement union detail
   * ("Demonstrated in: Session 1, 3" — spec §1.5).
   */
  const renderCrf = () => {
    if (!crfReadiness || !crfGate || crfStatements.length === 0) return null;
    const levelLabel = crfReadiness.level.toUpperCase();
    return panel(
      <>
        <Text selectable style={styles.panelTitle}>
          {t('icfProgress.crfReadiness.detailTitle', { level: levelLabel })}
        </Text>
        <CrfGateRow gate={crfGate} level={crfReadiness.level} showTitle={false} testID="competency_detail_crf_gate" />
        <View style={styles.crfList}>
          {crfStatements.map((statement) => {
            const status = crfReadiness.statements[statement.id];
            const demonstrated = Boolean(status?.demonstrated);
            const sessions = status?.sessions_demonstrated || [];
            const naOnly = !demonstrated && (status?.sessions_na?.length || 0) > 0;
            return (
              <View key={statement.id} style={styles.crfStatement}>
                <MaterialIcons
                  name={demonstrated ? 'check-circle' : 'radio-button-unchecked'}
                  size={18}
                  color={demonstrated ? reviewDesk.success : reviewDesk.muted}
                  style={styles.crfIcon}
                />
                <View style={styles.flexOne}>
                  <Text selectable style={styles.crfStatementText}>
                    <Text style={styles.crfStatementId}>{statement.id}</Text>
                    {'  '}
                    {language === 'tr' ? statement.text_tr : statement.text_en}
                  </Text>
                  {demonstrated && sessions.length > 0 && (
                    <Text selectable style={styles.crfSessions}>
                      {t('icfProgress.crfReadiness.demonstratedIn', { sessions: sessions.join(', ') })}
                    </Text>
                  )}
                  {naOnly && (
                    <Text selectable style={styles.crfNoOpportunity}>
                      {t('icfProgress.crfReadiness.noOpportunity')}
                    </Text>
                  )}
                </View>
              </View>
            );
          })}
        </View>
        <Text selectable style={styles.crfFootnote}>
          {t('icfProgress.crfReadiness.footnote', {
            level: levelLabel,
            revision: crfReadiness.form_revision,
          })}
        </Text>
      </>,
      'competency_detail_crf',
    );
  };

  const toggleCriteria = (level: string) => {
    setExpanded((current) => ({ ...current, [level]: !current[level] }));
    if (!expanded[level]) {
      trackAuthEvent('progress_competency_criteria_expanded', { competency, level });
    }
  };

  const renderCriteria = () => panel(
    <>
      <Text selectable style={styles.panelTitle}>{t('icfProgress.detail.levelCriteria')}</Text>
      {(['acc', 'pcc', 'mcc'] as const).map((level) => (
        <View key={level} style={styles.criteriaSection}>
          <Pressable
            testID={`competency_detail_criteria_${level}`}
            onPress={() => toggleCriteria(level)}
            accessibilityRole="button"
            accessibilityState={{ expanded: Boolean(expanded[level]) }}
            style={styles.criteriaHeader}
          >
            <Text selectable style={styles.criteriaTitle}>{level.toUpperCase()}</Text>
            <MaterialIcons
              name={expanded[level] ? 'expand-less' : 'expand-more'}
              size={22}
              color={reviewDesk.accent}
            />
          </Pressable>
          {expanded[level] && (
            <View style={styles.criteriaBody}>
              {detail?.level_criteria[level]?.length ? detail.level_criteria[level].map((criterion, index) => (
                <View key={`${level}-${index}`} style={styles.criterionRow}>
                  <View style={styles.bullet} />
                  <Text selectable style={[styles.bodyText, styles.flexOne]}>{criterion}</Text>
                </View>
              )) : (
                <Text selectable style={styles.emptyText}>{t('icfProgress.detail.noCriteria')}</Text>
              )}
            </View>
          )}
        </View>
      ))}
    </>,
    'competency_detail_criteria',
  );

  if (loading) {
    return <View style={styles.stateScreen}><ProgressBackButton /><View style={styles.center}><ActivityIndicator color={reviewDesk.accent} /></View></View>;
  }
  if (error || !detail) {
    return (
      <View style={styles.stateScreen} testID="competency_detail_error">
        <ProgressBackButton />
        <View style={styles.center}>
          <Text selectable style={styles.errorText}>{error || t('icfProgress.detail.loadError')}</Text>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      testID="competency_detail_screen"
      contentInsetAdjustmentBehavior="automatic"
      style={styles.screen}
      contentContainerStyle={[
        styles.content,
        isPhone && styles.contentPhone,
        isTablet && styles.contentTablet,
      ]}
    >
      <ProgressBackButton />
      <View style={styles.header}>
        <Text selectable style={[styles.title, isPhone && styles.titlePhone]}>{title}</Text>
        <View style={styles.headerMeta}>
          <Text selectable testID="competency_detail_level_chip" style={styles.levelChip}>
            {detail.lane.ethics_concern
              ? t('icfProgress.laneEthicsConcern')
              : detail.lane.competency_code === 'C1'
                ? t('sampleReportPolicy.c1Caption')
                : detail.lane.current_level || t('icfProgress.noData')}
          </Text>
          <View style={styles.momentumChip}>
            <MaterialIcons name={momentumIcon} size={16} color={reviewDesk.accent} />
            <Text selectable style={styles.momentumText}>{momentumLabel}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.columns, !isDesktop && styles.columnsStacked]}>
        <View style={styles.column}>
          {renderHistory()}
          {renderCrf()}
          {renderCriteria()}
        </View>
        <View style={styles.column}>
          {renderEvidence()}
          {renderPractice()}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: reviewDesk.canvas },
  content: { width: '100%', maxWidth: 1240, alignSelf: 'center', padding: 28, gap: 20 },
  contentTablet: { maxWidth: 760, paddingHorizontal: 24 },
  contentPhone: { padding: 16, gap: 16 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.canvas, padding: 24 },
  stateScreen: { flex: 1, backgroundColor: reviewDesk.canvas, padding: 20 },
  header: { gap: 12 },
  title: { color: reviewDesk.ink, fontSize: 32, lineHeight: 39, fontWeight: '800' },
  titlePhone: { fontSize: 25, lineHeight: 31 },
  headerMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  levelChip: { backgroundColor: reviewDesk.accentSoft, color: reviewDesk.accent, fontWeight: '700', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 999 },
  momentumChip: { flexDirection: 'row', gap: 5, alignItems: 'center', borderWidth: 1, borderColor: reviewDesk.line, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999 },
  momentumText: { color: reviewDesk.inkSoft, fontWeight: '600' },
  columns: { flexDirection: 'row', alignItems: 'flex-start', gap: 20 },
  columnsStacked: { flexDirection: 'column' },
  column: { flex: 1, width: '100%', gap: 20 },
  panel: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18, gap: 12 },
  panelTitle: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  panelSubtitle: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 20 },
  emptyState: { alignItems: 'center', paddingVertical: 22, gap: 10 },
  emptyText: { color: reviewDesk.muted, lineHeight: 20 },
  historyChart: { borderRadius: 14, backgroundColor: reviewDesk.accentWash, padding: 10, overflow: 'hidden' },
  historyDateRow: { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 44, marginTop: -15 },
  historyDate: { color: reviewDesk.muted, fontSize: 11, fontWeight: '600' },
  trendMessage: { color: reviewDesk.muted, fontSize: 12, lineHeight: 17 },
  evidenceCard: { borderLeftWidth: 3, backgroundColor: reviewDesk.cardMuted, borderRadius: 13, padding: 14, gap: 8 },
  evidenceMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  pill: { fontSize: 12, fontWeight: '800', textTransform: 'uppercase' },
  metaText: { color: reviewDesk.muted, fontSize: 12 },
  quote: { color: reviewDesk.ink, fontSize: 15, lineHeight: 22, fontStyle: 'italic' },
  showMoreButton: { minHeight: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 8 },
  showMoreText: { color: reviewDesk.accent, fontSize: 13, fontWeight: '800' },
  pressed: { opacity: 0.72 },
  practiceItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, paddingVertical: 4 },
  practiceNumber: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  practiceNumberText: { color: reviewDesk.accent, fontWeight: '800' },
  itemTitle: { color: reviewDesk.ink, fontSize: 15, fontWeight: '700', marginBottom: 5 },
  bodyText: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 21 },
  successText: { color: reviewDesk.success, fontSize: 13, lineHeight: 19, fontWeight: '600', marginTop: 7 },
  criteriaSection: { borderTopWidth: 1, borderTopColor: reviewDesk.line },
  criteriaHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 13 },
  criteriaTitle: { color: reviewDesk.ink, fontWeight: '800' },
  criteriaBody: { gap: 10, paddingBottom: 12 },
  criterionRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  bullet: { width: 6, height: 6, marginTop: 8, borderRadius: 3, backgroundColor: reviewDesk.accent },
  flexOne: { flex: 1 },
  crfList: { gap: 11, marginTop: 4 },
  crfStatement: { flexDirection: 'row', alignItems: 'flex-start', gap: 9 },
  crfIcon: { marginTop: 2 },
  crfStatementText: { color: reviewDesk.inkSoft, fontSize: 14, lineHeight: 20 },
  crfStatementId: { color: reviewDesk.ink, fontWeight: '800', fontVariant: ['tabular-nums'] },
  crfSessions: { color: reviewDesk.success, fontSize: 12, lineHeight: 17, fontWeight: '600', marginTop: 3 },
  crfNoOpportunity: { color: reviewDesk.amber, fontSize: 12, lineHeight: 17, fontWeight: '600', marginTop: 3 },
  crfFootnote: { color: reviewDesk.muted, fontSize: 11, lineHeight: 16, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: reviewDesk.line, paddingTop: 10 },
  errorText: { color: '#B42318', textAlign: 'center', lineHeight: 22 },
});
